import React, { useEffect, useState, useCallback } from 'react';
import { fetchCalendar } from '../api';

const VIEWS = [
  { id: 'today', label: 'Today',  days: 1  },
  { id: 'week',  label: 'Week',   days: 7  },
  { id: 'month', label: 'Month',  days: 30 },
];

const DAYS_FULL  = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function toLocalDateStr(isoString, allDay) {
  if (allDay) return isoString;
  const d = new Date(isoString);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

function formatTime(isoString, allDay) {
  if (allDay) return 'All day';
  const d = new Date(isoString);
  let h = d.getHours(), m = d.getMinutes();
  const ampm = h >= 12 ? 'pm' : 'am';
  h = h % 12 || 12;
  return `${h}${m ? ':' + String(m).padStart(2,'0') : ''}${ampm}`;
}

function formatDayHeading(dateStr) {
  const d = new Date(dateStr + 'T12:00:00');
  const today = todayStr();
  const tomorrow = (() => {
    const t = new Date(); t.setDate(t.getDate() + 1);
    return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`;
  })();
  if (dateStr === today)    return 'Today';
  if (dateStr === tomorrow) return 'Tomorrow';
  return `${DAYS_FULL[d.getDay()]}, ${MONTHS_SHORT[d.getMonth()]} ${d.getDate()}`;
}

// Build a list of date strings for the range
function getDateRange(days) {
  const dates = [];
  for (let i = 0; i < days; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    dates.push(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`);
  }
  return dates;
}

export default function CalendarPanel() {
  const [view, setView]     = useState('week');
  const [events, setEvents] = useState([]);
  const [error, setError]   = useState(null);
  const [loading, setLoading] = useState(true);

  const currentView = VIEWS.find(v => v.id === view);

  const load = useCallback(async () => {
    try {
      const data = await fetchCalendar(currentView.days);
      setEvents(data.events || []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [currentView.days]);

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [load]);

  // Group events by local date
  const dateRange = getDateRange(currentView.days);
  const grouped = dateRange.reduce((acc, dateStr) => {
    acc[dateStr] = events.filter(e => toLocalDateStr(e.start, e.all_day) === dateStr);
    return acc;
  }, {});

  // For today view, only show today
  const daysToShow = view === 'today'
    ? [todayStr()]
    : dateRange.filter(d => grouped[d]?.length > 0 || view === 'week');

  return (
    <div style={styles.panel}>

      {/* Toggle buttons */}
      <div style={styles.toggleRow}>
        {VIEWS.map(v => (
          <button
            key={v.id}
            style={{
              ...styles.toggleBtn,
              ...(view === v.id ? styles.toggleBtnActive : {}),
            }}
            onClick={() => setView(v.id)}
          >
            {v.label}
          </button>
        ))}
        <span style={styles.dot} />
      </div>

      {loading && <p style={styles.empty}>Loading…</p>}

      {error && (
        <p style={styles.error}>
          {error.includes('not configured') ? 'Google Calendar not set up yet' : 'Could not load calendar'}
        </p>
      )}

      {/* Event list grouped by day */}
      {!loading && !error && (
        <div style={styles.eventList}>
          {daysToShow.map(dateStr => {
            const dayEvents = grouped[dateStr] || [];
            const isToday = dateStr === todayStr();

            return (
              <div key={dateStr} style={styles.dayGroup}>
                {/* Day heading — only show in week/month view, or always show in today */}
                {(view !== 'today') && (
                  <div style={{
                    ...styles.dayHeading,
                    ...(isToday ? styles.dayHeadingToday : {}),
                  }}>
                    {formatDayHeading(dateStr)}
                    {isToday && <span style={styles.todayPip} />}
                  </div>
                )}

                {dayEvents.length === 0 ? (
                  view === 'today' ? (
                    <p style={styles.empty}>Nothing scheduled today</p>
                  ) : null
                ) : (
                  dayEvents.map(ev => (
                    <div key={ev.id} style={{
                        ...styles.event,
                        borderLeftColor: ev.color || 'var(--accent2)',
                      }}>
                      <span style={{
                        ...styles.eventTime,
                        color: ev.color || 'var(--accent2)',
                      }}>{formatTime(ev.start, ev.all_day)}</span>
                      <span style={styles.eventName}>{ev.summary}</span>
                    </div>
                  ))
                )}
              </div>
            );
          })}

          {/* Month view: show empty state if truly nothing */}
          {view === 'month' && events.length === 0 && (
            <p style={styles.empty}>No events this month</p>
          )}
        </div>
      )}
    </div>
  );
}

const styles = {
  panel: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    height: '100%',
    overflow: 'hidden',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  toggleBtn: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.65rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--text-dimmer)',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: 20,
    padding: '3px 10px',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  toggleBtnActive: {
    color: 'var(--accent2)',
    border: '1px solid var(--accent2)',
    background: 'rgba(122,184,232,0.08)',
  },
  dot: {
    marginLeft: 4,
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: 'var(--accent2)',
    boxShadow: '0 0 6px var(--accent2)',
    flexShrink: 0,
  },
  eventList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    overflowY: 'auto',
    flex: 1,
  },
  dayGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  dayHeading: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: 'var(--font-mono)',
    fontSize: '0.62rem',
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: 'var(--text-dimmer)',
    padding: '6px 2px 2px',
    borderTop: '1px solid var(--border)',
    marginTop: 4,
  },
  dayHeadingToday: {
    color: 'var(--accent2)',
    borderTopColor: 'rgba(122,184,232,0.3)',
  },
  todayPip: {
    width: 5,
    height: 5,
    borderRadius: '50%',
    background: 'var(--accent2)',
    boxShadow: '0 0 5px var(--accent2)',
  },
  event: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 10,
    padding: '7px 10px',
    background: 'var(--surface2)',
    borderRadius: 'var(--radius-sm)',
    borderLeft: '2px solid var(--accent2)',
  },
  eventTime: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.68rem',
    color: 'var(--accent2)',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  eventName: {
    fontSize: '0.88rem',
    color: 'var(--text)',
    lineHeight: 1.3,
  },
  empty: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
    color: 'var(--text-dimmer)',
    fontStyle: 'italic',
    padding: '8px 2px',
  },
  error: {
    fontFamily: 'var(--font-mono)',
    fontSize: '0.75rem',
    color: 'var(--danger)',
    padding: '8px 2px',
  },
};
